// Domain: Role Middleware
// HOC and hooks for role-based component protection

import React, { useContext, useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { roleService } from "./role.service";

// Context for role-based data
const RoleContext = React.createContext();

// Role Provider Component
export const RoleProvider = ({ children }) => {
    const { currentUser } = useAuth();
    const [isInitialized, setIsInitialized] = useState(false);
    // const [currentUserRole, setCurrentUserRole] = useState(null);
    // const [currentUserPermissions, setCurrentUserPermissions] = useState([]);

    useEffect(() => {
        const initializeRoleService = async () => {
            await roleService.initialize(currentUser);
            // setCurrentUserRole(roleService.getCurrentUserRole());
            // setCurrentUserPermissions(roleService.getCurrentUserPermissions());
            setIsInitialized(true);
        };

        initializeRoleService();
    }, [currentUser]);

    const contextValue = {
        roleService,
        isInitialized,
        currentUserRole: isInitialized ? roleService.getCurrentUserRole() : null,
        currentUserPermissions: isInitialized ? roleService.getCurrentUserPermissions() : []
    };

    return (
        <RoleContext.Provider value={contextValue}>
            {children}
        </RoleContext.Provider>
    );
};

// Hook to use role context
export const useRole = () => {
    const context = useContext(RoleContext);
    if (!context) {
        throw new Error("useRole must be used within a RoleProvider");
    }
    return context;
};

// HOC for role-based component protection
export const withRole = (WrappedComponent, requiredPermissions = [], fallbackComponent = null) => {
    return function WithRoleComponent(props) {
        const { roleService, isInitialized } = useRole();

        if (!isInitialized) {
            return <div>Cargando permisos...</div>;
        }

        // Check if user has required permissions
        const hasAccess = requiredPermissions.length === 0 || 
                     roleService.hasAnyPermission(requiredPermissions);

        if (!hasAccess) {
            return fallbackComponent || (
                <div style={{ padding: "20px", textAlign: "center" }}>
                    <h3>Acceso Denegado</h3>
                    <p>No tienes permisos para acceder a esta funcionalidad.</p>
                </div>
            );
        }

        return <WrappedComponent {...props} />;
    };
};

// Component wrapper for role-based rendering
export const WithRole = ({ children, allowed, fallback = null }) => {
    const { roleService, isInitialized } = useRole();

    if (!isInitialized) {
        return <div>Cargando permisos...</div>;
    }

    const hasAccess = allowed.includes(roleService.getCurrentUserRole());

    if (!hasAccess) {
        return fallback;
    }

    return children;
};

// Hook for permission checking
export const usePermission = (permission) => {
    const { roleService } = useRole();
    return roleService.hasPermission(permission);
};

// Hook for multiple permission checking
export const usePermissions = (_permissions) => {
    const { roleService } = useRole();
    return {
        hasPermission: (permission) => roleService.hasPermission(permission),
        hasAnyPermission: (perms) => roleService.hasAnyPermission(perms),
        hasAllPermissions: (perms) => roleService.hasAllPermissions(perms),
        currentPermissions: roleService.getCurrentUserPermissions()
    };
};

// Hook for opportunity access checking
export const useOpportunityAccess = (_opportunity) => {
    const { roleService } = useRole();
  
    return {
        canView: roleService.canAccessOpportunity(_opportunity),
        canEdit: roleService.canEditOpportunity(_opportunity),
        canManageTasks: roleService.canManageTasks()
    };
};

// Hook for KYC access checking
export const useKYCAccess = () => {
    const { roleService } = useRole();
    return {
        canView: roleService.canViewKYC(),
        canApprove: roleService.canApproveKYC()
    };
};

// Protected Route Component
export const ProtectedRoute = ({ children, requiredPermissions = [], fallback = null }) => {
    const { roleService, isInitialized } = useRole();

    if (!isInitialized) {
        return <div>Cargando permisos...</div>;
    }

    const hasAccess = requiredPermissions.length === 0 || 
                   roleService.hasAnyPermission(requiredPermissions);

    if (!hasAccess) {
        return fallback || (
            <div style={{ padding: "20px", textAlign: "center" }}>
                <h3>Acceso Denegado</h3>
                <p>No tienes permisos para acceder a esta página.</p>
            </div>
        );
    }

    return children;
};

// Conditional rendering component
export const ConditionalRender = ({ 
    children,
    condition,
    fallback = null,
    loadingComponent = <div>Cargando...</div>
}) => {
    const { isInitialized } = useRole();

    if (!isInitialized) {
        return loadingComponent;
    }

    if (!condition) {
        return fallback;
    }

    return children;
};

// Admin only guard
export const AdminGuard = ({ 
    children,
    fallback = null,
    loadingComponent = <div>Verificando permisos de administrador...</div>
}) => {
    const { isInitialized, currentUserRole } = useRole();

    if (!isInitialized) {
        return loadingComponent;
    }

    if ((currentUserRole || "").toUpperCase() !== "ADMIN") {
        return fallback || (
            <div style={{ padding: "20px", textAlign: "center" }}>
                <h3>Acceso Denegado</h3>
                <p>Solo los administradores pueden acceder a esta funcionalidad.</p>
            </div>
        );
    }

    return children;
}; 